import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { encryptJSON, decryptJSON, EncryptedPayload } from '../utils/crypto';
import { useLicenseStore } from '../stores/license';

// Domain model for an investor plot; UI-only state persisted locally
type Plot = {
  id: number;
  owned: boolean;
  tokens: number; // saved CO2 tokens assigned to this plot
};

// Serialized state container for encryption
type InvestorState = {
  plots: Plot[];
  updatedAt: string;
};

const STORAGE_KEY = 'evat_investor_encrypted';

export function PropertyGrid({ savedCo2Tokens = 0 }: { savedCo2Tokens?: number }) {
  const { t } = useTranslation();
  const { licenseKey, valid } = useLicenseStore();

  const [state, setState] = useState<InvestorState>({
    plots: Array.from({ length: 6 }, (_, i) => ({ id: i + 1, owned: false, tokens: 0 })),
    updatedAt: new Date().toISOString()
  });

  // Load from encrypted storage if license key exists
  useEffect(() => {
    (async () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw || !licenseKey) return;
        const payload: EncryptedPayload = JSON.parse(raw);
        const data = await decryptJSON<InvestorState>(licenseKey, payload);
        setState(data);
      } catch {}
    })();
  }, [licenseKey]);

  // Persist encrypted whenever state changes and license key is present
  useEffect(() => {
    (async () => {
      try {
        if (!licenseKey) return;
        const payload = await encryptJSON(licenseKey, state);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch {}
    })();
  }, [state, licenseKey]);

  const annualRate = 0.02;
  const totalTokens = useMemo(() => state.plots.reduce((a, p) => a + p.tokens, 0), [state]);
  const unassigned = Math.max(0, savedCo2Tokens - totalTokens);

  const toggleOwn = (id: number) => {
    if (!valid) return;
    setState((s) => ({
      ...s,
      plots: s.plots.map((p) => (p.id === id ? { ...p, owned: !p.owned } : p)),
      updatedAt: new Date().toISOString()
    }));
  };

  const addTokens = (id: number, amount: number) => {
    if (!valid) return;
    if (amount <= 0) return;
    setState((s) => ({
      ...s,
      plots: s.plots.map((p) => (p.id === id ? { ...p, tokens: p.tokens + amount } : p)),
      updatedAt: new Date().toISOString()
    }));
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-orbitron text-evat-neon text-xl">{t('investor.properties') || 'Virtual Properties'}</h3>
        <div className="text-sm text-neutral-400">
          {t('investor.unassigned') || 'Unassigned tokens'}: <span className="text-evat-gold">{unassigned.toFixed(2)}</span>
        </div>
      </div>

      {!valid && (
        <div className="mb-4 border border-evat-gold/40 rounded p-3 text-evat-gold">
          {t('license.invalidNotice') || 'Enter a valid license key to enable staking actions.'}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {state.plots.map((plot) => {
          const annualYield = plot.tokens * annualRate;
          return (
            <motion.div
              key={plot.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="border rounded p-4 bg-black/60 border-evat-neon/60 hover:shadow-neon transition-shadow"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-orbitron text-evat-neon">{t('investor.plot') || 'Plot'} #{plot.id}</div>
                <span className={`text-xs ${plot.owned ? 'text-evat-neon' : 'text-neutral-400'}`}>
                  {plot.owned ? (t('investor.owned') || 'Owned') : (t('investor.available') || 'Available')}
                </span>
              </div>
              <div className="text-sm text-neutral-300 mb-1">{t('investor.tokens') || 'Tokens'}: <span className="text-evat-gold">{plot.tokens.toFixed(2)}</span></div>
              <div className="text-sm text-neutral-300 mb-3">{t('investor.annualYield') || 'Annual yield (2%)'}: <span className="text-evat-gold">{annualYield.toFixed(2)}</span></div>

              <div className="flex gap-2">
                <button
                  disabled={!valid}
                  onClick={() => toggleOwn(plot.id)}
                  className={`px-3 py-1 rounded border ${valid ? 'border-evat-neon text-evat-neon hover:shadow-neon' : 'border-neutral-700 text-neutral-500'} transition-shadow`}
                >
                  {plot.owned ? (t('investor.release') || 'Release') : (t('investor.own') || 'Own')}
                </button>
                <button
                  disabled={!valid || unassigned <= 0}
                  onClick={() => addTokens(plot.id, Math.min(unassigned, savedCo2Tokens / 6))}
                  className={`px-3 py-1 rounded border ${valid && unassigned > 0 ? 'border-evat-gold text-evat-gold hover:shadow-neon' : 'border-neutral-700 text-neutral-500'} transition-shadow`}
                >
                  {t('investor.addFromSaved') || 'Add from Saved CO2'}
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export default PropertyGrid;


